import React, { useMemo } from 'react';
import { useOrganization } from 'context/OrganizationContext';
import { MareCycleCheck, MarecyclecheckService } from 'openapi';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from 'context/AccountContext';
import { TileList, TileRow } from 'ui/Layout/Tile';
import { eggLocationEnumToString, eggStateEnumToString, softnessEnumToString, uterusEnumToString } from 'utilities/Breeding';
import { Chat } from '@phosphor-icons/react';
import Tooltip from 'ui/Tooltip';
import { Label } from 'ui/Label';
import { Hint } from 'ui/Hint';
import Button, { ButtonSize, ButtonVariant } from 'ui/Button';
import useModal from 'ui/Modals/UseModal';
import ShowPreviousMareCycleChecksModal from './ShowPreviousMareCycleChecksModal';

interface ReturnType {
  element: ReactNode;
  clear: () => void;
}

interface Props {
  selectedMareUid: string | undefined;
  modalIsOpen: boolean;
}

/**
 * Small hook to show the last 3 previous mare cycle checks
 */
function useMareCyclePreviousRecords({ selectedMareUid, modalIsOpen }: Props): ReturnType {
  const [mareCycleChecks, setMareCycleChecks] = useState<{ items: MareCycleCheck[]; totalCount: number }>();

  const { closeModal, modalIsVisible, showModal } = useModal();
  const { selectedOrganizationUid } = useOrganization();
  const { t } = useTranslation();
  const { formatDate } = useAccount();

  /* Load the data from the API
   */
  const loadMareCycleChecks = useCallback(
    async (mareUid: string) => {
      try {
        if (!selectedOrganizationUid) return;
        const result = await MarecyclecheckService.marecyclecheckList({
          mareOrganisationUid: selectedOrganizationUid,
          mareUid,
          pageSize: 3,
          o: '-date,id',
        });

        setMareCycleChecks({ items: result.results, totalCount: result.count });
      } catch (error) {
        console.error('Error loading mareCycle', error);
      }
    },
    [selectedOrganizationUid],
  );

  /**
   * Load the mare cycles for the selected mare
   */
  useEffect(() => {
    if (selectedMareUid && modalIsOpen) {
      loadMareCycleChecks(selectedMareUid);
    } else if (!selectedMareUid) {
      setMareCycleChecks(undefined);
    }
  }, [loadMareCycleChecks, modalIsOpen, selectedMareUid]);

  const tableElement = useMemo(() => {
    if (!mareCycleChecks || mareCycleChecks.items.length === 0) return <></>;

    return (
      <>
        <div>
          <Label className='mb-2'>{t('history', 'History')}</Label>
          <Hint>
            {t('previous-3-mare-cycle-checks-desc', 'For the currently selected mare, we have found multiple previous mare cycle checks.')}
          </Hint>
          <div className='border p-2 bg-gray-50 rounded-lg'>
            <TileList>
              {mareCycleChecks.items.map(mareCycleCheck => (
                <TileRow key={mareCycleCheck.uid} className='!py-2 !px-2'>
                  <div className='flex gap-x-2 w-full'>
                    <p className='text-sm '>{mareCycleCheck.date && formatDate(new Date(Date.parse(mareCycleCheck.date)))}</p>
                    <div>
                      <div className='text-sm flex gap-x-2'>
                        <p>
                          <span className='font-bold'>{'U '}</span>
                          {uterusEnumToString(mareCycleCheck.uterus)}
                        </p>{' '}
                        -{' '}
                        <p>
                          <span className='font-bold'>{'C '}</span>
                          {softnessEnumToString(mareCycleCheck.cervix)}
                        </p>
                        {mareCycleCheck.extra_info && (
                          <Tooltip content={mareCycleCheck.extra_info} classNameContent='max-w-[300px]'>
                            <div className='flex gap-x-1'>
                              <Chat size={16} />
                            </div>
                          </Tooltip>
                        )}
                      </div>

                      {mareCycleCheck.egg_set && (
                        <>
                          {mareCycleCheck.egg_set.map(egg => (
                            <div key={egg.uid} className='flex gap-x-1 text-sm text-gray-500 italic'>
                              {eggLocationEnumToString(egg.location)}{' '}
                              {egg.size_metric && (
                                <>
                                  - {egg.size_metric} {t('cm-metric', 'cm')}
                                </>
                              )}{' '}
                              {egg.hardness && <>- {softnessEnumToString(egg.hardness)}</>}
                              {egg.egg_state && <>- {eggStateEnumToString(egg.egg_state)}</>}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </TileRow>
              ))}
            </TileList>

            {mareCycleChecks && mareCycleChecks.totalCount > 3 && (
              <div className='flex'>
                <Button type='button' variant={ButtonVariant.Default} size={ButtonSize.XSmall} onClick={showModal}>
                  {t('show-more', 'Show more')}
                </Button>
              </div>
            )}
          </div>
        </div>

        {selectedMareUid && (
          <ShowPreviousMareCycleChecksModal isVisible={modalIsVisible} onRequestCloseModal={closeModal} mareUid={selectedMareUid} />
        )}
      </>
    );
  }, [closeModal, formatDate, mareCycleChecks, modalIsVisible, selectedMareUid, showModal, t]);

  return {
    element: tableElement,
    clear: () => setMareCycleChecks(undefined),
  };
}

export default useMareCyclePreviousRecords;
